import { html, el, trusted } from './html.js';
import { state } from './state.js';
import { back } from './router.js';
import { plugMark, SETTINGS, BACK } from './icons.js';

const TABS = [['/', 'Feed'], ['/map', 'Map'], ['/add', 'Add']];

// One screen: top bar, the one-line confirmation (role=status), the body, and for a
// signed-in screen the bottom bar. Feed, Map and Add show the wordmark and settings;
// other screens show a back arrow.
export function screen({ tab = null, back: withBack = false, bars = true, className = '' } = {}) {
  const root = el(html`<div class="screen ${className}">
    <header class="topbar">
      ${withBack
        ? html`<button class="icon-btn" type="button" aria-label="Back">${trusted(BACK)}</button>`
        : html`<a class="wordmark" href="/">${trusted(plugMark({ small: true }))}<span>Shteker</span></a>`}
      ${bars && !withBack ? html`<a class="icon-btn" href="/settings" aria-label="Settings">${trusted(SETTINGS)}</a>` : ''}
    </header>
    <p class="flash" role="status" ${state.flash ? '' : trusted('hidden')}>${state.flash}</p>
    <main class="body"></main>
    ${bars ? html`<nav class="tabs" aria-label="Main">${TABS.map(([href, label]) =>
      html`<a href="${href}" ${tab === label ? trusted('aria-current="page"') : ''}><span>${label}</span></a>`)}</nav>` : ''}
  </div>`);
  root.querySelector('[aria-label=Back]')?.addEventListener('click', back);
  const flash = root.querySelector('.flash');
  return {
    el: root,
    body: root.querySelector('.body'),
    status(text) { flash.textContent = text; flash.hidden = !text; },
  };
}

export function errorScreen(message) {
  const s = screen({ back: !!state.user, bars: !!state.user });
  s.body.append(el(html`<p class="stack">${message}</p>`));
  return s.el;
}

// The 2 px copper upload line across the top. It shows a sliver at once, because a
// proxy or a slow start can hold back the first progress event.
export function progress() {
  const line = el(html`<div class="progress" role="progressbar" aria-label="Upload progress"></div>`);
  document.body.append(line);
  const set = fraction => line.style.setProperty('--p', String(Math.max(0.02, fraction)));
  set(0);
  return { set, done: () => line.remove() };
}

// A one-line message on whichever screen is showing now, for work that finished after the
// member had left the screen that started it.
export function notify(text) {
  const flash = document.querySelector('#app .flash');
  if (!flash) return;
  flash.textContent = text;
  flash.hidden = !text;
}

// For work that can outlast its screen (photos being prepared or sent): the member may leave
// meanwhile. Call it first thing in the view, before any await, so `here` is the path the view
// was opened on, as typed. Navigation changes the path at once, but the router replaces the
// screen only when the next one's data has arrived, so a message told meanwhile waits for it.
// `cleanup` belongs in the screen's own cleanup.
export function watchLeaving() {
  const here = location.pathname;
  let gone = false; // the screen was taken down
  let waiting = null;
  return {
    here,
    left: () => gone || location.pathname !== here,
    tell(text) {
      if (gone) notify(text);
      else waiting = text;
    },
    cleanup() {
      gone = true;
      // The router puts the next screen in right after this call.
      if (waiting) queueMicrotask(() => notify(waiting));
    },
  };
}
